import React, { useState, useRef } from "react";
import {
  FormGroup,
  Label,
  ErrorMessage,
  FormDescription,
} from "./FormComponents";
import { formText } from "./FormText";
import { Box, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { NoticeboardButton } from "../shared";
import { lightTheme } from "../../styles/theme";
import { MAX_VIDEO_UPLOAD_MB } from "../../constants/uploadLimits";
import { NoticeUploadProgress } from "../shared/NoticeUploadProgress";
import { NoticeComposerUploadedFileRow } from "./NoticeComposerUploadedFileRow";
import type { NoticeUploadProgressState } from "../../utils/presignedUpload";

// Utility functions
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

interface StepProps {
  formData: {
    title: string;
    body: string;
    type: string;
    audience: string;
    attachmentUrl: string;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    imageUrl?: string;
    eventBackgroundImage?: string;
  };
  errors: {
    title?: string;
    body?: string;
    type?: string;
    audience?: string;
    attachmentUrl?: string;
    eventStartDate?: string;
    eventStartTime?: string;
    eventEndDate?: string;
    eventEndTime?: string;
    eventLocation?: string;
  };
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: any } }
  ) => void;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleFileRemove?: (index: number) => void;
  setNoticeQualityScore?: (score: number) => void;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
}

export const AttachmentStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  handleFileUpload,
  handleFileRemove,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFileUpload?.(e.dataTransfer.files);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await handleFileUpload?.(e.target.files);
    }
  };

  const hasFile = formData.uploadedFiles && formData.uploadedFiles.length > 0;
  const fileCount = formData.uploadedFiles?.length || 0;
  const maxFiles = 20;
  const canAddMore = fileCount < maxFiles;

  return (
    <FormGroup>
      <Label>{formText.labels.attachment}</Label>
      <FormDescription>{formText.descriptions.attachment}</FormDescription>

      {/* File Upload Area */}
      <Box
        sx={{
          border: "2px dashed",
          borderColor: dragActive ? "primary.main" : "divider",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          backgroundColor: dragActive ? "primary.50" : "background.paper",
          transition: "all 0.2s ease",
          cursor: canAddMore ? "pointer" : "not-allowed",
          opacity: canAddMore ? 1 : 0.5,
          pointerEvents: canAddMore ? "auto" : "none",
          "&:hover": {
            borderColor: canAddMore ? "primary.main" : "divider",
            backgroundColor: canAddMore ? "primary.50" : "background.paper",
          },
        }}
        onDragEnter={canAddMore ? handleDrag : undefined}
        onDragLeave={canAddMore ? handleDrag : undefined}
        onDragOver={canAddMore ? handleDrag : undefined}
        onDrop={canAddMore ? handleDrop : undefined}
        onClick={canAddMore ? () => fileInputRef.current?.click() : undefined}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, color: "text.primary" }}>
          {hasFile
            ? canAddMore
              ? `Add more files (${fileCount}/${maxFiles})`
              : `Maximum ${maxFiles} attachments reached`
            : "Drop files here or click to upload"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Supports PDF, images, documents, spreadsheets, and videos (videos up to {MAX_VIDEO_UPLOAD_MB}MB,
          images up to 25MB, other files up to 10MB). Up to {maxFiles} files per notice.
        </Typography>
        <NoticeboardButton
          variant="secondary"
          disabled={!canAddMore || isUploading}
          onClick={(e) => {
            e.stopPropagation();
            if (canAddMore) fileInputRef.current?.click();
          }}
        >
          Choose File{hasFile ? "s" : ""}
        </NoticeboardButton>
        <input
          ref={fileInputRef}
          type="file"
          multiple={true}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.txt,.doc,.docx,.xls,.xlsx,.csv,.mp4,.mpeg,.mov,.avi,.wmv,.3gp"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
      </Box>

      {isUploading && (
        <NoticeUploadProgress
          progress={uploadProgress ?? null}
          statusMessage={uploadStatusMessage}
          onCancel={onCancelUpload}
        />
      )}

      {/* Uploaded Files */}
      {hasFile && formData.uploadedFiles && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Uploaded Files ({fileCount}/{maxFiles})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {formData.uploadedFiles.map((file, index) => (
              <NoticeComposerUploadedFileRow
                key={`${file.fileUrl}-${index}`}
                file={file}
                index={index}
                onRemove={handleFileRemove}
                formatSize={formatFileSize}
                dense
              />
            ))}
          </Box>
        </Box>
      )}

      {errors.attachmentUrl && (
        <ErrorMessage>{errors.attachmentUrl}</ErrorMessage>
      )}
    </FormGroup>
  );
};

export const ImageUploadStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleFileUpload,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const [preview, setPreview] = useState<string | null>(
    formData.imageUrl || ""
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (handleFileUpload) await handleFileUpload(e.target.files);
    }
  };

  return (
    <FormGroup>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
      >
        Upload Image
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Select an image to include in your notice (max 7MB).
      </Typography>
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleImageChange}
      />
      <NoticeboardButton
        variant="primary"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        sx={{ mb: 2 }}
      >
        {isUploading ? "Processing..." : "Choose Image"}
      </NoticeboardButton>
      {isUploading && (
        <NoticeUploadProgress
          progress={uploadProgress ?? null}
          statusMessage={uploadStatusMessage}
          onCancel={onCancelUpload}
          sx={{ mb: 2 }}
        />
      )}
      {preview && (
        <Box sx={{ mt: 2, mb: 2, textAlign: "center" }}>
          <img
            src={preview}
            alt="Notice attachment preview"
            style={{
              maxWidth: "100%",
              maxHeight: 240,
              borderRadius: 8,
              boxShadow: `0 2px 8px ${lightTheme.colors.shadowMedium}`,
            }}
          />
        </Box>
      )}
      {errors.attachmentUrl && (
        <ErrorMessage>{errors.attachmentUrl}</ErrorMessage>
      )}
    </FormGroup>
  );
};
